import {
  NgbNav,
  NgbNavItem,
  NgbNavItemRole,
  NgbNavLink,
  NgbNavLinkBase,
  NgbNavContent,
  NgbScrollSpy,
  NgbScrollSpyFragment,
  NgbDropdownModule,
  NgbNavOutlet,
} from '@ng-bootstrap/ng-bootstrap';
import { Component, computed, inject, Injector, OnInit, Signal, ViewChild } from '@angular/core';
import {
  CreateUpdateCustomerCreditNoteDto,
  CustomerCreditNoteDto,
  CustomerCreditNoteService,
  CustomerDocDefaultValueDto,
  DayBookService,
} from '@digiwall/goliath-documents/proxy';
import { ClassificationTypeService, DocRole } from '@digiwall/goliath-core/proxy';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ConfigStateService,
  LocalizationService,
  SubscriptionService,
  LocalizationModule,
} from '@abp/ng.core';
import { BreadcrumbService } from 'xng-breadcrumb';
import { Confirmation, ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { FormBuilder, Validators, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { DocTemplateService } from '../../base-docs/detail/doc-template-selection/doc-template.service';
import { asyncScheduler, map, Observable, scheduled } from 'rxjs';
import { CustomerService, ThirdPartyDto } from '@digiwall/goliath-contacts/proxy';
import { GoliathContactsEnumerationTypeStore, SalesPersonStore } from '@digiwall/goliath-contacts';
import { CustomerCreditNoteLinesEditorComponent } from './customer-credit-note-lines/customer-credit-note-lines-editor.component';
import {
  AnnexesListComponent,
  GoliathCoreEnumerationTypeStore,
  SharedModule,
} from '@digiwall/goliath-core';
import { GoliathDocumentsEnumerationTypeStore } from '../../../enumeration-types/enumeration-type.store';
import { ReportViewerComponent } from '@odyssey/reporting';
import { FieldMapping } from '../../base-third-party-docs/base-third-party-doc-detail';
import {
  ChatComponent,
  DeactivatableComponentService,
  HistoryComponent,
  OdysseyOffcanvas,
} from '@digiwall/core';
import {
  CounterComponent,
  DialogBoxComponent,
  DropdownMenu,
  DropdownMenuComponent,
  PanelComponent,
} from '@digiwall/ui-kit';
import { DwGridDetailNavigationComponent, DwTabsComponent } from '@digiwall/ag-grid';
import { DwNavLinkDirective } from '@digiwall/core';
import { NgxValidateCoreModule } from '@ngx-validate/core';
import { DwKeyboardShortcutDirective } from '@digiwall/core';
import { DwScrollSpyDirective } from '@digiwall/ag-grid';
import { DocConditionsComponent } from '../../base-docs/detail/doc-conditions/doc-conditions.component';
import { DocClassificationsComponent } from '../../base-docs/detail/doc-classifications/doc-classifications.component';
import { DocCustomerAddressesComponent } from '../../base-docs/detail/doc-customer-addresses/doc-customer-addresses.component';
import { DocCustomerOthersComponent } from '../../base-docs/detail/doc-customer-others/doc-customer-others.component';
import { AsyncPipe } from '@angular/common';
import { SidePanelComponent } from '@digiwall/ui-kit';
import { DocSidePanelComponent } from '../../base-docs/detail/doc-ui-components/doc-side-panel/doc-side-panel.component';
import {
  BaseThirdPartyDocLineUpdatedDto,
  DocNumberTagComponent,
  DocumentCoreCardComponent,
  DocumentCustomerCardComponent,
  DocumentCustomerDateCardComponent,
  DocumentCustomerStatusCardComponent,
} from '../../../components';
import {
  CustomerCreditNotePermission,
  DocReportPermission,
} from '@digiwall/goliath-documents/config';
import { BaseInvoiceDocDetail } from '../../base-invoice-docs/base-invoice-doc-detail';
import { firstValueFrom } from 'rxjs';

@Component({
  selector: 'document-customer-credit-note-detail',
  templateUrl: './customer-credit-note-detail.component.html',
  styleUrl: './customer-credit-note-detail.component.scss',
  providers: [DocTemplateService],
  imports: [
    SharedModule,
    PanelComponent,
    DwTabsComponent,
    NgbNav,
    NgbNavItem,
    NgbNavItemRole,
    NgbNavLink,
    NgbNavLinkBase,
    DwNavLinkDirective,
    NgbNavContent,
    NgbScrollSpy,
    ReactiveFormsModule,
    NgxValidateCoreModule,
    DwKeyboardShortcutDirective,
    NgbScrollSpyFragment,
    DwScrollSpyDirective,
    CustomerCreditNoteLinesEditorComponent,
    DocConditionsComponent,
    DocClassificationsComponent,
    DocCustomerAddressesComponent,
    DocCustomerOthersComponent,
    AnnexesListComponent,
    NgbDropdownModule,
    NgbNavOutlet,
    SidePanelComponent,
    FormsModule,
    ReportViewerComponent,
    AsyncPipe,
    LocalizationModule,
    CounterComponent,
    ChatComponent,
    DwGridDetailNavigationComponent,
    DocSidePanelComponent,
    DocumentCoreCardComponent,
    DocumentCustomerCardComponent,
    DocumentCustomerDateCardComponent,
    DocumentCustomerStatusCardComponent,
    DialogBoxComponent,
    DropdownMenuComponent,
    HistoryComponent,
    DocNumberTagComponent,
  ],
})
export class CustomerCreditNoteDetailComponent
  extends BaseInvoiceDocDetail<
    CustomerCreditNoteService,
    CustomerCreditNoteDto,
    CreateUpdateCustomerCreditNoteDto,
    CustomerDocDefaultValueDto
  >
  implements OnInit
{
  salesPersonStore = inject(SalesPersonStore);
  enumerationTypeStore = inject(GoliathDocumentsEnumerationTypeStore);
  contactEnumerationTypeStore = inject(GoliathContactsEnumerationTypeStore);
  coreEnumerationTypeStore = inject(GoliathCoreEnumerationTypeStore);

  @ViewChild('customerCreditNoteLineGrid') docLineGrid: CustomerCreditNoteLinesEditorComponent;
  @ViewChild('report') reportViewer: ReportViewerComponent;

  docRole = DocRole.CustomerCreditNote;

  isDueDateExpired = false;

  protected saveActionBtns = computed(() => {
    let btns = [
      {
        label: this.localizationService.instant('::Save'),
        action: () => this.save(),
        disabled: () => !this.form.dirty,
      },
    ];
    return btns;
  });

  actions: Signal<DropdownMenu> = computed(() => {
    return {
      groups: [
        this.baseActionsThirdPartyDoc().concat(
          {
            label: '::GeneratePdf',
            icon: 'digi-out-pdf-file',
            action: () => this.generatePdf(this.reportViewer),
          },
          {
            label: 'Doc::PrintDocument',
            icon: 'digi-out-print',
            action: () => this.printDocument(),
            enabled: this.canPrintDocument,
          },
          {
            label: 'Doc::CustomerInvoice:GenerateUbl',
            icon: 'digi-out-invoice',
            action: () => {
              return firstValueFrom(this.docService.createUBLFile(this.entity.id));
            },
          },
          {
            label: 'Doc::CustomerCreditNote:Reopen',
            icon: 'digi-out-unlock',
            action: () => {
              this.confirmationService
                .warn('Doc::CustomerCreditNote:ReopenConfirmation', '::AreYouSure')
                .subscribeOnce(x => {
                  if (x != Confirmation.Status.confirm) return;
                  this.customerCreditNoteService
                    .reopen(this.docId)
                    .subscribeOnce(() => this.getEntity(this.docId));
                });
            },
            visible: computed(() =>
              this.permissionHelper.checkPermission(CustomerCreditNotePermission.Override),
            ),
          },
        ),
        [
          {
            label: 'Doc::GenerateDocPrintTemplate',
            icon: 'digi-out-edit-square',
            action: () => this.openDesigner(),
            requiredPolicy: DocReportPermission.Create,
          },
        ],
      ],
    };
  });

  remainingAmount$: Observable<number>;

  constructor(
    route: ActivatedRoute,
    router: Router,
    subscriptionService: SubscriptionService,
    private customerCreditNoteService: CustomerCreditNoteService,
    formBuilder: FormBuilder,
    toastService: ToasterService,
    breadcrumbService: BreadcrumbService,
    dayBookService: DayBookService,
    localizationService: LocalizationService,
    injector: Injector,
    customerService: CustomerService,
    config: ConfigStateService,
    docTemplateService: DocTemplateService,
    classificationTypeService: ClassificationTypeService,
    confirmationService: ConfirmationService,
    deactivableComponentService: DeactivatableComponentService,
  ) {
    super(
      route,
      router,
      subscriptionService,
      customerCreditNoteService,
      formBuilder,
      toastService,
      breadcrumbService,
      dayBookService,
      localizationService,
      classificationTypeService,
      docTemplateService,
      injector,
      customerService,
      config,
      confirmationService,
      deactivableComponentService,
    );
  }

  override get defaultValue() {
    return Object.assign(this.baseDefaultValue, {
      isExported: false,
      allowPartialDelivery: false,
    });
  }

  override get formModel() {
    return Object.assign({}, super.formModel, {
      dueDate: [null, Validators.required],
      isExported: [false, Validators.required],
      invoicingAddress_ThirdPartyAddressId: [null],
    });
  }

  protected override initFormObservers() {
    super.initFormObservers();

    this.form.get('paymentTermId').valueChanges.subscribe(paymentTermId => {
      const documentDate = this.form.get('documentDate')?.value;
      this.computeDueDate(documentDate, paymentTermId);
    });
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.init();
  }

  protected override setDefaultValue(response: CustomerDocDefaultValueDto) {
    super.setDefaultValue(response);
    this.computeDueDate(response.documentDate, null);
  }

  protected computeDueDate(documentDate?: any, paymentTermId?: any) {
    if (documentDate == null) return;

    this.docService
      .calculateDueDate({
        docDate: documentDate,
        paymentTermId: paymentTermId,
      })
      .subscribe(dueDate => {
        this.form.get('dueDate').setValue(dueDate.date);
      });
  }

  getEntityCallback(): void {
    super.getEntityCallback();
    super.loadThirdParties(this.entity?.isClosed);

    this.docLineGrid?.lineGrid?.grid?.grid?.api?.redrawRows();
  }

  protected override afterSave(): void {
    this.isDueDateExpired = this.entity.dueDate < new Date().toISOString();
    super.afterSave();
  }

  protected override onThirdPartyChanged = (thirdPartyId: string): void => {
    super.onThirdPartyChanged(thirdPartyId);
    this.thirdPartyId$.next(thirdPartyId);
  };

  protected override getFieldMappings(): FieldMapping[] {
    return [
      ...super.getFieldMappings(),
      { formField: 'representativeCommision', customerField: 'representativeCommission' },
    ];
  }

  protected fetchAccountingData(docId: string, thirdPartyId: string): void {
    this.setRemainingAmount$(docId);
    this.isDueDateExpired = this.entity.dueDate < new Date().toISOString();

    super.fetchAccountingData(docId, thirdPartyId);
  }

  private setRemainingAmount$(docId: string) {
    if (this.entity.balanceDue != null) {
      this.remainingAmount$ = scheduled([this.entity.balanceDue], asyncScheduler);
    } else if (this.config.getGlobalFeatureIsEnabled('Goliath.Accounting')) {
      this.remainingAmount$ = this.docService
        .getAccountingCreditNoteOutstanding(docId)
        .pipe(map(response => response?.openAmount));
    }
  }

  protected override applyThirdPartyConfiguration(thirdParty: ThirdPartyDto) {
    const documentConfiguration = this.currentCompanyStore.currentCompany()?.documentsConfiguration;
    if (documentConfiguration?.paymentTermId != null) {
      this.form.get('paymentTermId')?.setValue(documentConfiguration.paymentTermId);
    }
    if (!documentConfiguration.useCustomerFinancialDiscountInReturnWorkflow) {
      this.form.get('financialDiscountDays')?.setValue(null);
      this.form.get('financialDiscount')?.setValue(null);
    }
  }

  protected override refreshAfterLineUpdate(
    params: Partial<BaseThirdPartyDocLineUpdatedDto>,
  ): void {
    super.refreshAfterLineUpdate(params);
    if (params.balanceDue != null) {
      this.entity.balanceDue = params.balanceDue;
      this.setRemainingAmount$(this.docId);
    }
  }
}
